// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { OrderCancellationReason, OrdersDeliveryType, OrdersStatus } from '@/models/orders.model';

// Components
import { StyledTitleModal, StyledSelect, StyledButton } from '../UpdateOrderPaymentModal/UpdateOrderPaymentModalStyles';
import { Modal, ModalSize } from '@/components';
import CancellationReasonFields from '../CancellationReasonFields/CancellationReasonFields';

// Helpers
import { getStatusOptions } from '../../helpers';

interface UpdateOrderStatusModalProps {
  isOpenUpdateOrderStatusModal: boolean;
  isLoading: boolean;
  currentStatus: OrdersStatus;
  deliveryType?: OrdersDeliveryType;
  closeUpdateOrderStatusModal: () => void;
  handleUpdateStatus: (
    status?: OrdersStatus,
    cancellationReason?: OrderCancellationReason,
    cancellationReasonText?: string,
  ) => void;
}

const UpdateOrderStatusModal = ({
  isOpenUpdateOrderStatusModal,
  isLoading,
  currentStatus,
  deliveryType,
  closeUpdateOrderStatusModal,
  handleUpdateStatus,
}: UpdateOrderStatusModalProps) => {
  const { t } = useTranslation(['orders']);
  const options = getStatusOptions(t, deliveryType);
  const [selectedOption, setSelectedOption] = useState(options.find((el) => el.value === currentStatus)?.value);
  const [reason, setReason] = useState<OrderCancellationReason | undefined>();
  const [reasonText, setReasonText] = useState('');

  useEffect(() => {
    if (isOpenUpdateOrderStatusModal) {
      setSelectedOption(currentStatus);
      setReason(undefined);
      setReasonText('');
    }
  }, [isOpenUpdateOrderStatusModal, currentStatus]);

  const onSelectChange = (value: unknown) => {
    const nextStatus = value as OrdersStatus;
    setSelectedOption(nextStatus);

    if (nextStatus !== OrdersStatus.CANCELLED) {
      setReason(undefined);
      setReasonText('');
    }
  };

  const isCancelledSelected = selectedOption === OrdersStatus.CANCELLED;
  const isOtherWithoutText = reason === OrderCancellationReason.OTHER && !reasonText.trim();
  const isConfirmDisabled =
    isLoading ||
    !selectedOption ||
    selectedOption === currentStatus ||
    (isCancelledSelected && (!reason || isOtherWithoutText));

  const onConfirm = () => {
    if (isConfirmDisabled) {
      return;
    }

    handleUpdateStatus(
      selectedOption,
      isCancelledSelected ? reason : undefined,
      isCancelledSelected && reason === OrderCancellationReason.OTHER ? reasonText.trim() : undefined,
    );
  };

  return (
    <Modal visible={isOpenUpdateOrderStatusModal} onCancel={closeUpdateOrderStatusModal} size={ModalSize.LARGE}>
      <StyledTitleModal>{t('update_order_status_modal_title')}</StyledTitleModal>
      <StyledSelect
        options={options}
        placeholder={t('orders_status_select_placeholder')}
        onChange={onSelectChange}
        value={selectedOption}
        size="large"
        allowClear
      />
      {isCancelledSelected && (
        <CancellationReasonFields
          reason={reason}
          reasonText={reasonText}
          onReasonChange={setReason}
          onReasonTextChange={setReasonText}
        />
      )}
      <StyledButton onClick={onConfirm} disabled={isConfirmDisabled} type="primary">
        {t('update_order_status_modal_button')}
      </StyledButton>
    </Modal>
  );
};

export default UpdateOrderStatusModal;
